// What the build line says while the model is busy (DES1-specs "builder busy/throttled
// states" and "Try with another model"). A wait, not a failure: the draft is saved, the dot is
// a still ring, the actions are the slow line's quiet buttons. Never an automatic switch, a
// restart, a wordless spinner or a toast; another model only as the person's own choice.
import { formatCopy } from '@/i18n/ns';
import type { BuildExperienceCopyKey } from '@/i18n/ns/build-experience';
import { PHASE_COPY } from './line-model';
import type { BuildAlternative, BuildWait, DisplayAction } from './vm';

type Copy = Record<BuildExperienceCopyKey, string>;

const REFUSED_COPY: Record<
  Extract<BuildAlternative, { status: 'refused' }>['reason'],
  BuildExperienceCopyKey
> = {
  policy: 'altPolicy',
  region: 'altRegion',
  budget: 'altBudget',
  'not-ready': 'altNotReady',
};

export type WaitLine = {
  /** The first sentence: the line's title. */
  title: string;
  /** What happens next, under the title; null when the title says it all. */
  note: string | null;
  /** 'ring': waiting, the dot without its pulse. 'live': checking, the breathing dot. 'quiet': stopped. */
  dot: 'ring' | 'live' | 'quiet';
  /** The server's retry action, labelled "Try now" or "Try again". */
  retry: { action: DisplayAction; label: string } | null;
  /** "Keep waiting": only while it waits to continue by itself. */
  keepWaiting: boolean;
  /** "Try with <model>" when admission offers it, or admission's reason in one muted line. */
  alternative: { action: DisplayAction; label: string } | { reason: string } | null;
  /** Stop here (the VM's stop action) belongs with these actions. */
  stop: boolean;
};

/** Whole seconds until the server's retry-after, never below zero. */
export function secondsUntil(retryAt: string, now: number): number {
  const at = Date.parse(retryAt);
  return Number.isFinite(at) ? Math.max(0, Math.ceil((at - now) / 1000)) : 0;
}

/** The one explicit alternative, as admission decided it; nothing when none is offered. */
export function alternativeOf(
  alternative: BuildAlternative | null,
  copy: Copy,
): WaitLine['alternative'] {
  if (!alternative) return null;
  return alternative.status === 'offered'
    ? { action: alternative.action, label: formatCopy(copy.tryWith, { model: alternative.model.shortName }) }
    : { reason: copy[REFUSED_COPY[alternative.reason]] };
}

export function waitLine(wait: BuildWait, copy: Copy, now: number, locale: string): WaitLine {
  // The step's own label, quoted by the catalog: never lowercased, so every language keeps its grammar.
  const step = (phase: keyof typeof PHASE_COPY) => copy[PHASE_COPY[phase]];
  const base = {
    note: null,
    dot: 'ring',
    retry: null,
    keepWaiting: false,
    alternative: null,
    stop: true,
  } as const;
  switch (wait.kind) {
    case 'admission': {
      const n = secondsUntil(wait.retryAt, now);
      const when =
        n > 0
          ? formatCopy(copy.retryIn, { n: new Intl.NumberFormat(locale).format(n) })
          : copy.retryingNow;
      return {
        ...base,
        title: formatCopy(copy.busyNow, { model: wait.model.shortName }),
        note: `${copy.draftSaved} ${when}`,
        retry: wait.retry ? { action: wait.retry, label: copy.tryNow } : null,
        alternative: alternativeOf(wait.alternative, copy),
      };
    }
    case 'reconciling':
      // Nothing new starts, and no other model is offered, until the last step is reconciled.
      return { ...base, title: copy.checkingLastStep, dot: 'live', stop: false };
    case 'held':
      return {
        ...base,
        title: formatCopy(copy.busyHeld, { model: wait.model.shortName }),
        note: `${copy.draftSaved} ${formatCopy(copy.continueFrom, { step: step(wait.step), model: wait.model.shortName })}`,
        keepWaiting: true,
        alternative: alternativeOf(wait.alternative, copy),
      };
    case 'exhausted':
      return {
        ...base,
        title: formatCopy(copy.busyStill, { model: wait.model.shortName }),
        note: `${copy.draftSaved} ${copy.tryLater}`,
        retry: wait.retry ? { action: wait.retry, label: copy.tryAgain } : null,
        alternative: alternativeOf(wait.alternative, copy),
      };
    case 'stopped':
      return {
        ...base,
        title: formatCopy(copy.stoppedAt, { step: step(wait.step) }),
        dot: 'quiet',
        stop: false,
      };
  }
}
